/**
 * The surfaces this deployment serves.
 *
 * ## Build-time, and fail-closed when absent
 *
 * `VITE_WEB_ORIGIN_*` are baked in when the bundle is built; nothing can supply them at runtime. A
 * hosted build that omits them therefore has no correct value to fall back to — and the fallback it
 * used to have was `http://localhost:5173`, which is how staging shipped a bundle that completed a
 * sign-in and then refused to navigate: *"That destination is not a surface this deployment
 * serves"*. The session was real, the destination was real, and the bundle disagreed with its own
 * deployment.
 *
 * So a missing origin yields nothing rather than a guess. In production the list simply does not
 * contain that surface, and a return navigation to it is refused — the safe direction, and one that
 * shows up immediately rather than sending somebody to a URL this deployment does not own.
 *
 * The development defaults live behind `import.meta.env.DEV`, which the production build replaces
 * with `false` and then removes along with the literals. That is what lets the bundle proof assert
 * the shipped artifact contains no localhost origin at all: not that the fallback was unused, but
 * that it is not there.
 */
const DEV_ORIGINS = {
  attendee: 'http://localhost:5173',
  organizer: 'http://localhost:5174',
  ops: 'http://localhost:5175',
} as const;

function surfaceOrigin(configured: unknown, devFallback: string): string | null {
  if (typeof configured === 'string' && configured.length > 0) {
    try {
      return new URL(configured).origin;
    } catch {
      // A malformed origin is not a surface. Refusing beats normalising something unparseable
      // into an allowlist entry.
      return null;
    }
  }
  return import.meta.env.DEV ? new URL(devFallback).origin : null;
}

/**
 * Exact origins, never patterns.
 *
 * An allowlist with a wildcard is not an allowlist, and the destination this is checked against
 * comes back from the server — which validated it before the browser ever left. This is defence in
 * depth on top of that, never a substitute for it.
 */
export const CONFIGURED_ORIGINS: readonly string[] = [
  surfaceOrigin(import.meta.env.VITE_WEB_ORIGIN_ATTENDEE, DEV_ORIGINS.attendee),
  surfaceOrigin(import.meta.env.VITE_WEB_ORIGIN_ORGANIZER, DEV_ORIGINS.organizer),
  surfaceOrigin(import.meta.env.VITE_WEB_ORIGIN_OPS, DEV_ORIGINS.ops),
].filter((origin): origin is string => origin !== null);

/** Whether a destination names a surface this deployment actually serves. */
export function servesOrigin(destination: URL): boolean {
  return CONFIGURED_ORIGINS.includes(destination.origin);
}
